import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Headphones, Clock, Undo2, Printer, ArrowLeftRight, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fmtDate, today, errMsg, waLink, recogidaTomatisMsg, devolucionTomatisMsg } from "@/lib/api";
import { useCentro } from "@/lib/centro";

const API = "/tomatis-devices";

const WaRecogida = ({ tid, tel, nombre, ciclo, fecha, avisado, testId, onAviso, tipo = "recogida" }) => {
  const [av, setAv] = useState(avisado);
  useEffect(() => {
    setAv(avisado);
  }, [avisado]);
  if (!tel) return null;
  const marcar = async (v) => {
    await api.post(`${API}/aviso-recogida/${tid}`, { ciclo, avisado: v, tipo });
    setAv(v ? today() : null);
    onAviso?.();
    if (!v) toast.success("Aviso desmarcado");
  };
  if (av) {
    return (
      <span data-testid={`${testId}-avisado`} className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 whitespace-nowrap">
        <Check className="h-3 w-3" />Avisado {fmtDate(av).slice(0, 5)}
        <button type="button" onClick={(e) => { e.preventDefault(); marcar(false); }} data-testid={`${testId}-undo`} title="Desmarcar (no se ha avisado)" className="ml-0.5 text-emerald-700 hover:text-rose-600"><X className="h-3 w-3" /></button>
      </span>
    );
  }
  return (
    <a href={waLink(tel, (tipo === "devolucion" ? devolucionTomatisMsg : recogidaTomatisMsg)(nombre, ciclo, fecha))} target="_blank" rel="noreferrer" data-testid={testId} title={tipo === "devolucion" ? "Recordar que devuelva el aparato (queda marcado como avisado)" : "Avisar para recoger el aparato (queda marcado como avisado)"}
      onClick={() => marcar(true)} className="rounded bg-[#25D366] px-2 py-1 text-[11px] font-medium text-white whitespace-nowrap hover:opacity-90">WhatsApp</a>
  );
};

const Siguiente = ({ d, espera, onSig, onFecha, onAviso }) => (
  <label className="text-[11px] text-slate-500 flex flex-col gap-0.5">Siguiente que lo coge
    <select data-testid={`tdev-siguiente-${d.id}`} className="field-select h-7 text-xs py-0" value={d.siguiente?.treatment_id || ""} onChange={(e) => onSig(d, e.target.value)}>
      <option value="">— Sin decidir —</option>
      {espera.map((t) => <option key={t.id} value={t.id}>{t.patient.nombre} · ciclo {t.ciclo}{t.fecha ? ` · ${fmtDate(t.fecha)}` : ""}{t.reservado && t.reservado !== d.nombre ? ` · ya en ${t.reservado}` : ""}</option>)}
    </select>
    {d.siguiente && (
      <span className="flex items-center gap-1 mt-0.5">Empieza
        <input type="date" value={d.siguiente.fecha || ""} onChange={(e) => onFecha(d, e.target.value)} data-testid={`tdev-siguiente-fecha-${d.id}`} className="h-6 min-w-0 flex-1 rounded border border-slate-200 px-1 text-[11px] text-slate-700" />
        <WaRecogida tid={d.siguiente.treatment_id} avisado={d.siguiente.avisado} tel={d.siguiente.telefono} nombre={d.siguiente.nombre} ciclo={d.siguiente.ciclo} fecha={d.siguiente.fecha} testId={`tdev-wa-${d.id}`} onAviso={onAviso} />
      </span>
    )}
  </label>
);

const Card = ({ d, espera, onAssign, onReturn, onSig, onFecha, onCambiar, onAviso }) => {
  const t = d.treatment;
  return (
    <div data-testid={`tdev-${d.id}`} className={`rounded-lg border p-3 flex flex-col gap-2 ${!t ? "bg-emerald-50/60 border-emerald-200" : d.retrasado ? "bg-rose-50 border-rose-200" : "bg-white border-violet-200"}`}>
      <div className="flex items-center justify-between">
        <span className="font-[Outfit] font-semibold text-slate-900 text-sm">{d.nombre}</span>
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${!t ? "text-emerald-700" : d.retrasado ? "text-rose-700" : "text-violet-700"}`}>{!t ? "Libre" : d.retrasado ? "Retrasado" : "Prestado"}</span>
      </div>
      {t ? (
        <>
          <Link to={`/tratamiento/${t.id}`} className="text-sm text-[#1E3A8A] hover:underline truncate" data-testid={`tdev-patient-${d.id}`}>{t.patient?.nombre}</Link>
          <div className="text-[11px] text-slate-500">Ciclo {d.ciclo || "?"} · desde {fmtDate(d.fecha_prestamo)}{t.patient?.centro ? ` · ${t.patient.centro}` : ""}</div>
          <div className={`text-[11px] flex items-center gap-1 ${d.retrasado ? "text-rose-700 font-semibold" : "text-slate-500"}`} data-testid={`tdev-fin-${d.id}`}>
            <span className="flex-1">Entrega prevista {fmtDate(d.fin_prevista)}</span>
            <WaRecogida tipo="devolucion" tid={t.id} avisado={d.avisado_devolucion} tel={t.patient?.telefono} nombre={t.patient?.nombre} ciclo={d.ciclo} fecha={d.fin_prevista} testId={`tdev-wa-dev-${d.id}`} onAviso={onAviso} />
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs flex-1 px-1" data-testid={`tdev-return-${d.id}`} onClick={() => onReturn(d)}><Undo2 className="h-3 w-3 mr-1" />Devuelto</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs flex-1 px-1" data-testid={`tdev-change-${d.id}`} onClick={() => onCambiar(d)}><ArrowLeftRight className="h-3 w-3 mr-1" />Cambiar</Button>
          </div>
        </>
      ) : (
        <Button size="sm" className="h-7 text-xs bg-violet-700 hover:bg-violet-800" data-testid={`tdev-assign-${d.id}`} onClick={() => onAssign({ device: d, tid: d.siguiente?.treatment_id })}>Prestar{d.siguiente ? ` a ${d.siguiente.nombre.split(" ")[0]}` : ""}</Button>
      )}
      <Siguiente d={d} espera={espera} onSig={onSig} onFecha={onFecha} onAviso={onAviso} />
    </div>
  );
};

const ChangeDialog = ({ sel, devices, onClose, onDone }) => {
  const [nid, setNid] = useState("");
  useEffect(() => {
    setNid("");
  }, [sel]);
  const submit = async () => {
    if (!nid) return toast.error("Elige el otro aparato");
    try {
      const { data } = await api.post(`${API}/${sel.id}/cambiar`, { nuevo_device_id: nid });
      toast.success(data.intercambio ? `Intercambiados ${data.de} y ${data.a}` : `Cambiado de ${data.de} a ${data.a}. ${data.de} queda libre.`);
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!sel} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="tdev-change-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">Cambiar aparato</DialogTitle></DialogHeader>
        {sel && <p className="text-sm text-slate-600"><b>{sel.treatment?.patient?.nombre}</b> tiene ahora <b>{sel.nombre}</b>. Elige un aparato libre para pasárselo, o uno prestado para intercambiar los pacientes. Se mantienen las fechas y el ciclo de cada uno.</p>}
        <div className="grid grid-cols-2 gap-2" data-testid="tdev-change-options">
          {devices.filter((d) => sel && d.id !== sel.id).map((d) => (
            <button key={d.id} type="button" data-testid={`tdev-change-to-${d.id}`} onClick={() => setNid(d.id)}
              className={`rounded-md border px-2 py-2 text-left text-xs transition-colors ${nid === d.id ? "bg-violet-700 border-violet-700 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>
              <span className="block font-semibold">{d.nombre}</span>
              <span className="block text-[11px] opacity-80 truncate">{d.treatment ? `Intercambiar con ${d.treatment.patient?.nombre}` : "Libre"}</span>
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="tdev-change-cancel">Cancelar</Button>
          <Button onClick={submit} data-testid="tdev-change-submit" className="bg-violet-700 hover:bg-violet-800">Cambiar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AssignDialog = ({ sel, devices, espera, onClose, onDone }) => {
  const [tid, setTid] = useState("");
  const [did, setDid] = useState("");
  const [fecha, setFecha] = useState(today());
  useEffect(() => {
    if (!sel) return;
    setTid(sel.tid || espera.find((t) => !t.reservado)?.id || espera[0]?.id || "");
    setDid(sel.device?.id || devices.find((d) => !d.treatment)?.id || "");
    setFecha(today());
  }, [sel, devices, espera]);
  const submit = async () => {
    if (!tid || !did) return toast.error("Elige paciente y aparato");
    try {
      const { data } = await api.post(`${API}/${did}/asignar`, { treatment_id: tid, fecha });
      toast.success(`Aparato prestado para el ciclo ${data.ciclo}`);
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!sel} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="tdev-assign-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">Prestar aparato Tomatis</DialogTitle></DialogHeader>
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Paciente
          <select data-testid="tdev-assign-patient" className="field-select mt-1" value={tid} onChange={(e) => setTid(e.target.value)}>
            {espera.length === 0 && <option value="">No hay pacientes esperando ciclo</option>}
            {espera.map((t) => <option key={t.id} value={t.id}>{t.patient.nombre} · ciclo {t.ciclo}{t.fecha ? ` · ${fmtDate(t.fecha)}` : ""}</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Aparato
          <select data-testid="tdev-assign-device" className="field-select mt-1" value={did} onChange={(e) => setDid(e.target.value)}>
            {devices.filter((d) => !d.treatment).map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Fecha de préstamo (se entrega a los 13 días)
          <Input type="date" data-testid="tdev-assign-fecha" value={fecha} onChange={(e) => setFecha(e.target.value)} className="mt-1" />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button data-testid="tdev-assign-confirm" onClick={submit} className="bg-violet-700 hover:bg-violet-800">Prestar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Espera = ({ espera, canAssign, onAssign, onAviso }) => (
  <div data-testid="tdev-espera" className="bg-white border border-slate-200 rounded-lg p-4">
    <div className="flex items-center gap-2 mb-1">
      <Clock className="h-4 w-4 text-slate-500" />
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Esperando ciclo (por fecha)</h3>
      <span className="ml-auto font-[JetBrains_Mono] text-xs text-slate-400">{espera.length}</span>
    </div>
    <p className="text-[11px] text-slate-400 mb-2">Tomatis activos sin aparato, ordenados por la fecha de su próximo ciclo.</p>
    <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
      {espera.length === 0 && <p className="text-sm text-slate-400 py-2">Nadie esperando.</p>}
      {espera.map((t, i) => (
        <div key={t.id} data-testid={`tdev-espera-row-${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 text-sm">
          <Link to={`/tratamiento/${t.id}`} className="basis-full truncate text-slate-800 hover:underline">{t.patient.nombre}</Link>
          <span className="text-[11px] text-slate-500 whitespace-nowrap">ciclo {t.ciclo} · {t.fecha ? fmtDate(t.fecha) : "sin fecha"}</span>
          <WaRecogida tid={t.id} avisado={t.avisado} tel={t.patient.telefono} nombre={t.patient.nombre} ciclo={t.ciclo} fecha={t.fecha} testId={`tdev-espera-wa-${i}`} onAviso={onAviso} />
          {t.reservado && <span className="rounded bg-violet-100 text-violet-800 text-[10px] font-semibold px-1.5 py-0.5 whitespace-nowrap">Para {t.reservado}</span>}
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!canAssign} data-testid={`tdev-espera-assign-${i}`} style={{ marginLeft: "auto" }} onClick={() => onAssign({ tid: t.id })}>Prestar</Button>
        </div>
      ))}
    </div>
  </div>
);

export const TomatisDevices = ({ onChange }) => {
  const { centro } = useCentro();
  const [devices, setDevices] = useState([]);
  const [espera, setEspera] = useState([]);
  const [esperaAll, setEsperaAll] = useState([]);
  const [sel, setSel] = useState(null);
  const [cambio, setCambio] = useState(null);
  const load = useCallback(() => {
    api.get(API).then((r) => setDevices(r.data));
    api.get(`${API}/espera`, { params: { centro } }).then((r) => setEspera(r.data));
    api.get(`${API}/espera`).then((r) => setEsperaAll(r.data));
  }, [centro]);
  useEffect(() => {
    load();
  }, [load]);
  const refresh = () => { load(); onChange?.(); };
  const onReturn = async (d) => {
    if (!window.confirm(`¿Confirmas que ${d.treatment?.patient?.nombre} ha devuelto ${d.nombre}? Su ciclo ${d.ciclo || ""} quedará marcado como hecho.`)) return;
    const { data } = await api.post(`${API}/${d.id}/devolver`, { fecha: today() });
    toast.success(`${d.nombre} vuelve a estar libre${data.ciclo ? ` · ciclo ${data.ciclo} marcado como hecho` : ""}`);
    refresh();
  };
  const onSig = async (d, tid) => {
    await api.post(`${API}/${d.id}/siguiente`, { treatment_id: tid });
    load();
  };
  const onFecha = async (d, fecha) => {
    await api.post(`${API}/${d.id}/siguiente-fecha`, { fecha });
    load();
  };
  const libres = devices.filter((d) => !d.treatment).length;
  return (
    <div data-testid="tomatis-devices" className="mb-8 fade-up">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Headphones className="h-4 w-4 text-violet-700" />
        <h2 className="font-[Outfit] text-lg font-semibold text-slate-900">Control de aparatos Tomatis</h2>
        <span className="text-xs text-slate-500" data-testid="tdev-summary">{libres} libres de {devices.length} · {espera.length} esperando ciclo · cada ciclo 13 días</span>
        <Link to="/tomatis/imprimir-aparatos" target="_blank" data-testid="tdev-print-btn" className="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50"><Printer className="h-3.5 w-3.5" />Imprimir control de aparatos</Link>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 content-start">
          {devices.map((d) => <Card key={d.id} d={d} espera={esperaAll} onAssign={setSel} onReturn={onReturn} onSig={onSig} onFecha={onFecha} onCambiar={setCambio} onAviso={load} />)}
        </div>
        <Espera espera={espera} canAssign={libres > 0} onAssign={setSel} onAviso={load} />
      </div>
      <AssignDialog sel={sel} devices={devices} espera={esperaAll} onClose={() => setSel(null)} onDone={refresh} />
      <ChangeDialog sel={cambio} devices={devices} onClose={() => setCambio(null)} onDone={refresh} />
    </div>
  );
};
